// login/src/components/AssessmentScore.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Alert,
  Grid,
  Tooltip,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Snackbar,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Home,
  ChevronRight,
  UploadFile,
  Save,
  Download,
  Search,
  Add,
  CloudUpload,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { updateScore, savePreview, getPreview } from "../api/tnsdc";

const AssessmentScore = () => {
  const navigate = useNavigate();
  const { batchCode } = useParams();
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
    loading: false,
  });
  const [openForm, setOpenForm] = useState(false);
  const [isTableEdited, setIsTableEdited] = useState(false);

  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const openDownloadMenu = Boolean(downloadAnchorEl);

  const handleOpenDownloadMenu = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleCloseDownloadMenu = () => {
    setDownloadAnchorEl(null);
  };

  const [formData, setFormData] = useState({
    batchCode: batchCode || "",
    batchID: "",
    candidateId: "",
    candidateName: "",
    enrolmentNo: "",
    assessmentResult: "",
    assessmentStatus: "",
    totalTheoryMarks: "",
    totalPracticalMarks: "",
    totalVivaMarks: "",
    totalMarks: "",
    assessmentResultPercentage: "",
    placeOfIssue: "",
    scoreArray: [
      {
        nosName: "",
        nosTheoryMarks: "",
        nosVivaMarks: "",
        nosPracticalMarks: "",
        nosTotalMarks: "",
      },
    ],
  });

  //------------Table row selection------------

  const [selectedRows, setSelectedRows] = useState([]);

  const toggleRowSelection = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex)
        ? prev.filter((i) => i !== rowIndex)
        : [...prev, rowIndex]
    );
  };

  const handleToggleAll = () => {
    const totalRows = filteredRows.slice(1).length;
    if (selectedRows.length === totalRows) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredRows.slice(1).map((_, idx) => idx));
    }
  };

  // --- Table cell edit ---
  const handleCellChange = (rowIndex, cellIndex, value) => {
    const updatedRows = [...rows];
    updatedRows[rowIndex][cellIndex] = value;
    setRows(updatedRows);
    setIsTableEdited(true);
  };

  const handleSaveScores = async () => {
    console.log("Saving only selected rows:", selectedRows);
    const dataRows = filteredRows.slice(1);
    const selectedData = selectedRows.map((i) => dataRows[i]);
    console.log("Selected rows data:", selectedData);
    setIsTableEdited(false);
    setSnackbar({
      open: true,
      message: "Changes saved successfully!",
      severity: "success",
      loading: false,
    });
  };

  // --- Pagination ---
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- Download template ---
  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/templates/Assessment_Score_template.xlsx";
    link.download = "Assessment_Score_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- File select ---
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0] ?? null;
    if (file) {
      setFileName(file.name);
      setSelectedFile(file);
    }
  };

  // --- Upload file ---
  const handleUploadConfirm = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!jsonData || jsonData.length === 0) {
          console.warn("Uploaded file contains no data.");
          setIsUploading(false);
          return;
        }

        setRows(jsonData);
        try {
          await savePreview({ rows: jsonData });
          console.log("Preview rows saved to DB");
        } catch (err) {
          console.error("Failed to save preview rows:", err);
        }

        setShowAlert(true);
        setOpenUploadDialog(false);

        setTimeout(() => setShowAlert(false), 5000);
      } catch (err) {
        console.error("Error processing uploaded file:", err);
      } finally {
        setSelectedFile(null);
        setFileName("");
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  const handlePublish = async () => {
    const dataRows = filteredRows.slice(1);
    const selectedData = selectedRows.map((i) => dataRows[i]);

    if (selectedData.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one row to publish.",
        severity: "warning",
        loading: false,
      });
      return;
    }

    setSnackbar({
      open: true,
      message: "Publishing assessment scores...",
      severity: "info",
      loading: true,
    });

    const payloads = selectedData.map((row) => {
      let nosMappingDetails = [];
      for (let i = 12; i < row.length; i += 2) {
        if (row[i] && row[i + 1] !== undefined) {
          nosMappingDetails.push({ nosCode: row[i], nosMark: row[i + 1] });
        }
      }
      return {
        batchCode: row[0],
        batchID: row[1],
        candidateId: row[2],
        candidateName: row[3],
        enrolmentNo: row[4],
        assessmentResult: row[5],
        assessmentStatus: row[6],
        totalTheoryMarks: row[7],
        totalVivaMarks: row[8],
        totalPracticalMarks: row[9],
        totalMarks: row[10],
        assessmentResultPercentage: row[11],
        placeOfIssue: row[12],
        scoreArray: nosMappingDetails,
      };
    });

    try {
      for (const payload of payloads) {
        await updateScore({
          updateCandidateScore: {
            batchCode: payload.batchCode,
            candidateArray: [payload],
          },
        });
      }

      setSnackbar({
        open: true,
        message: "Assessment scores published successfully!",
        severity: "success",
        loading: false,
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to publish assessment scores.",
        severity: "error",
        loading: false,
      });
      console.error(err);
    }
  };

  // --- Form handlers ---
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNOSChange = (index, field, value) => {
    const updated = [...formData.scoreArray];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, scoreArray: updated }));
  };

  const addNOSField = () => {
    setFormData((prev) => ({
      ...prev,
      scoreArray: [
        ...prev.scoreArray,
        {
          nosName: "",
          nosTheoryMarks: "",
          nosVivaMarks: "",
          nosPracticalMarks: "",
          nosTotalMarks: "",
        },
      ],
    }));
  };

  const removeNOSField = (index) => {
    const updated = formData.scoreArray.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, scoreArray: updated }));
  };

  const handleFormSubmit = async () => {
    const row = [
      formData.batchCode,
      formData.batchID,
      formData.candidateId,
      formData.candidateName,
      formData.enrolmentNo,
      formData.assessmentResult,
      formData.assessmentStatus,
      formData.totalTheoryMarks,
      formData.totalVivaMarks,
      formData.totalPracticalMarks,
      formData.totalMarks,
      formData.assessmentResultPercentage,
      formData.placeOfIssue,
      ...formData.scoreArray.flatMap((s) => [
        s.nosName,
        s.nosTheoryMarks,
        s.nosVivaMarks,
        s.nosPracticalMarks,
        s.nosTotalMarks,
      ]),
    ];

    let newRows = [...rows];

    if (rows.length === 0) {
      const headers = [
        "Batch Code",
        "Batch ID",
        "Candidate ID",
        "Candidate Name",
        "Enrolment No",
        "Assessment Result",
        "Assessment Status",
        "Total Theory Marks",
        "Total Viva Marks",
        "Total Practical Marks",
        "Total Marks",
        "Assessment Result Percentage",
        "Place of issue",
      ];

      formData.scoreArray.forEach((_, i) => {
        headers.push(
          `NOS Name -${i + 1}`,
          `NOS Theory Marks -${i + 1}`,
          `NOS Viva Marks -${i + 1}`,
          `NOS Practical Marks -${i + 1}`,
          `NOS Total Marks -${i + 1}`
        );
      });
      newRows = [headers, row];
    } else {
      const currentNOSCount = (rows[0].length - 12) / 5;
      const newNOSCount = formData.scoreArray.length;
      if (newNOSCount > currentNOSCount) {
        const updatedHeaders = [...rows[0]];
        for (let i = currentNOSCount; i < newNOSCount; i++) {
          updatedHeaders.push(
            `NOS Name -${i + 1}`,
            `NOS Theory Marks -${i + 1}`,
            `NOS Viva Marks -${i + 1}`,
            `NOS Practical Marks -${i + 1}`,
            `NOS Total Marks -${i + 1}`
          );
        }
        newRows[0] = updatedHeaders;
      }
      newRows.push(row);
    }

    setRows(newRows);
    setOpenForm(false);

    // build candidate object to match backend parsing expectations
    const candidateObj = {
      batchCode: formData.batchCode,
      batchID: formData.batchID ? Number(formData.batchID) : null,
      candidateId: formData.candidateId,
      candidateName: formData.candidateName,
      enrolmentNo: formData.enrolmentNo,
      assessmentResult: formData.assessmentResult,
      assessmentStatus: formData.assessmentStatus,
      totalTheoryMarks: formData.totalTheoryMarks
        ? Number(formData.totalTheoryMarks)
        : null,
      totalPracticalMarks: formData.totalPracticalMarks
        ? Number(formData.totalPracticalMarks)
        : null,
      totalVivaMarks: formData.totalVivaMarks
        ? Number(formData.totalVivaMarks)
        : null,
      totalMarks: formData.totalMarks ? Number(formData.totalMarks) : null,
      assessmentResultPercentage: formData.assessmentResultPercentage
        ? Number(formData.assessmentResultPercentage)
        : null,
      placeOfIssue: formData.placeOfIssue,
      scoreArray: formData.scoreArray.map((s) => ({
        nosName: s.nosName,
        nosTheoryMarks: s.nosTheoryMarks ? Number(s.nosTheoryMarks) : null,
        nosVivaMarks: s.nosVivaMarks ? Number(s.nosVivaMarks) : null,
        nosPracticalMarks: s.nosPracticalMarks
          ? Number(s.nosPracticalMarks)
          : null,
        nosTotalMarks: s.nosTotalMarks ? Number(s.nosTotalMarks) : null,
      })),
    };

    try {
      await savePreview({ candidate: candidateObj });
      console.log("Preview candidate saved to DB");
    } catch (err) {
      console.error("Failed to save preview candidate:", err);
    }

    // Reset form
    setFormData({
      batchCode: batchCode || "",
      batchID: "",
      candidateId: "",
      candidateName: "",
      enrolmentNo: "",
      assessmentResult: "",
      assessmentStatus: "",
      totalTheoryMarks: "",
      totalPracticalMarks: "",
      totalVivaMarks: "",
      totalMarks: "",
      assessmentResultPercentage: "",
      placeOfIssue: "",
      scoreArray: [
        {
          nosName: "",
          nosTheoryMarks: "",
          nosVivaMarks: "",
          nosPracticalMarks: "",
          nosTotalMarks: "",
        },
      ],
    });
  };

  // --- Filtered rows for search ---
  const filteredRows = rows.filter((row, index) => {
    if (index === 0) return true;
    return row.some(
      (cell) =>
        cell && cell.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  useEffect(() => {
    if (!batchCode) return;

    const fetchPreview = async () => {
      try {
        const res = await getPreview(batchCode);
        console.log("API preview raw response:", res);

        // normalize to candidate array
        let data = [];
        if (!res) {
          data = [];
        } else if (Array.isArray(res)) {
          // backend returned plain array
          data = res;
        } else if (
          res.updateCandidateScore &&
          Array.isArray(res.updateCandidateScore.candidateArray)
        ) {
          data = res.updateCandidateScore.candidateArray;
        } else if (Array.isArray(res.candidateArray)) {
          data = res.candidateArray;
        } else if (
          res.data &&
          Array.isArray(res.data.updateCandidateScore?.candidateArray)
        ) {
          // in case getPreview returned axios response object instead of r.data
          data = res.data.updateCandidateScore.candidateArray;
        } else {
          // nothing recognized — log and bail
          console.warn("Unrecognized preview response shape, response:", res);
          data = [];
        }

        if (data.length === 0) {
          // clear rows so UI shows "No assessment data uploaded"
          setRows([]);
          return;
        }

        // ensure each candidate has consistent keys and defaults
        const normalized = data.map((c) => ({
          batchID: c.batchID ?? c.batchId ?? c.batch_id ?? null,
          batchCode: c.batchCode ?? c.batch_code ?? "",
          candidateId: c.candidateId ?? c.candidate_id ?? c.candidateId ?? "",
          candidateName: c.candidateName ?? c.candidate_name ?? "",
          enrolmentNo: c.enrolmentNo ?? c.enrolment_no ?? "",
          assessmentResult: c.assessmentResult ?? c.assessment_result ?? "",
          assessmentStatus: c.assessmentStatus ?? c.assessment_status ?? "",
          totalTheoryMarks:
            c.totalTheoryMarks ??
            c.total_theory_marks ??
            c.totalTheoryMarks ??
            null,
          totalPracticalMarks:
            c.totalPracticalMarks ?? c.total_practical_marks ?? null,
          totalVivaMarks: c.totalVivaMarks ?? c.total_viva_marks ?? null,
          totalMarks: c.totalMarks ?? c.total_marks ?? null,
          assessmentResultPercentage:
            c.assessmentResultPercentage ??
            c.assessment_result_percentage ??
            null,
          placeOfIssue:
            c.placeOfIssue === undefined ||
            c.placeOfIssue === null ||
            (typeof c.placeOfIssue === "string" &&
              c.placeOfIssue.toLowerCase?.() === "n/a")
              ? ""
              : c.placeOfIssue ?? c.place_of_issue ?? "",
          scoreArray: Array.isArray(c.scoreArray)
            ? c.scoreArray
            : c.score_array
            ? c.score_array
            : [],
          published:
            c.published === true ||
            c.published === "Y" ||
            c.published === 1 ||
            false,
        }));

        // find max NOS count across candidates
        const maxNos = Math.max(
          ...normalized.map((c) => (c.scoreArray || []).length || 0)
        );

        // build header row 
        const headers = [
          "Batch Code",
          "Batch ID",
          "Candidate ID",
          "Candidate Name",
          "Enrolment No",
          "Assessment Result",
          "Assessment Status",
          "Total Theory Marks",
          "Total Viva Marks",
          "Total Practical Marks",
          "Total Marks",
          "Assessment Result Percentage",
          "Place of issue",
        ];

        for (let i = 0; i < maxNos; i++) {
          headers.push(
            `NOS Name - ${i + 1}`,
            `NOS Theory Marks - ${i + 1}`,
            `NOS Viva Marks - ${i + 1}`,
            `NOS Practical Marks - ${i + 1}`,
            `NOS Total Marks - ${i + 1}`
          );
        }
        headers.push("Publish Status");

        // build rowsData
        const rowsData = normalized.map((c) => {
          const baseValues = [
            c.batchCode,
            c.batchID,
            c.candidateId,
            c.candidateName,
            c.enrolmentNo,
            c.assessmentResult,
            c.assessmentStatus,
            c.totalTheoryMarks,
            c.totalVivaMarks,
            c.totalPracticalMarks,
            c.totalMarks,
            c.assessmentResultPercentage,
            c.placeOfIssue,
          ];

          const nosValues = [];
          (c.scoreArray || []).forEach((s) => {
            nosValues.push(
              s.nosName ?? s.nos_name ?? "",
              s.nosTheoryMarks ??
                s.nos_theory_marks ??
                s.nosTheoryMarks ??
                null,
              s.nosVivaMarks ?? s.nos_viva_marks ?? s.nosVivaMarks ?? null,
              s.nosPracticalMarks ??
                s.nos_practical_marks ??
                s.nosPracticalMarks ??
                null,
              s.nosTotalMarks ?? s.nos_total_marks ?? s.nosTotalMarks ?? null
            );
          });

          // pad missing NOS columns so every row has same length as headers
          while (nosValues.length < maxNos * 5) nosValues.push("");
          return [
            ...baseValues,
            ...nosValues,
            c.published ? "Published" : "Not Published",
          ];
        });

        setRows([headers, ...rowsData]);
      } catch (err) {
        console.error("Failed to fetch preview:", err);
        setRows([]); // ensure UI shows no-data
      }
    };

    fetchPreview();
  }, [batchCode]);

  // --- Common helper to get selected or all rows ---
  const getExportData = () => {
    if (!rows || rows.length === 0) {
      setSnackbar({
        open: true,
        message: "No data available to download.",
        severity: "warning",
        loading: false,
      });
      return null;
    }

    const headerRow = rows[0];
    const dataRows = filteredRows.slice(1);
    const selectedData =
      selectedRows.length > 0 ? selectedRows.map((i) => dataRows[i]) : dataRows;

    if (selectedData.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one row to download preview.",
        severity: "warning",
        loading: false,
      });
      return null;
    }

    return [headerRow, ...selectedData];
  };

  // --- Excel Download ---
  const handleDownloadExcel = () => {
    const exportData = getExportData();
    if (!exportData) return;

    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Preview");

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    XLSX.writeFile(
      wb,
      `Assessment_Preview_${batchCode || "data"}_${timestamp}.xlsx`
    );

    setSnackbar({
      open: true,
      message: "Excel file downloaded successfully!",
      severity: "success",
      loading: false,
    });
  };

  // --- JSON Download ---
  const handleDownloadJSON = () => {
    const exportData = getExportData();
    if (!exportData) return;

    const [headers, ...data] = exportData;
    const jsonArray = data.map((row) => {
      const obj = {};
      headers.forEach((h, i) => {
        obj[h] = row[i];
      });
      return obj;
    });

    const blob = new Blob([JSON.stringify(jsonArray, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Assessment_Preview_${batchCode || "data"}.json`;
    link.click();

    setSnackbar({
      open: true,
      message: "JSON file downloaded successfully!",
      severity: "success",
      loading: false,
    });
  };

  // --- PDF Download ---
  const handleDownloadPDF = async () => {
    const exportData = getExportData();
    if (!exportData) return;

    // Dynamically import jsPDF and autotable
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;

    const doc = new jsPDF({ orientation: "landscape" });
    const [headers, ...data] = exportData;

    doc.setFontSize(14);
    doc.text(`Assessment Preview - ${batchCode || ""}`, 14, 15);

    autoTable(doc, {
      startY: 25,
      head: [headers],
      body: data,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [0, 123, 255] },
    });

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    doc.save(`Assessment_Preview_${batchCode || "data"}_${timestamp}.pdf`);

    setSnackbar({
      open: true,
      message: "PDF file downloaded successfully!",
      severity: "success",
      loading: false,
    });
  };

  return (
    <Box p={3}>
      <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          color="inherit"
          onClick={() => navigate("/")}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" /> Dashboard
        </Link>
        <Typography color="text.primary">
          Assessment Score - {batchCode}
        </Typography>
      </Breadcrumbs>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="600">
          Assessment Scores
        </Typography>
        <Chip
          label={`Batch Code: ${batchCode}`}
          variant="outlined"
          color="primary"
        />
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={3} mb={3}>
        <Tooltip title="Upload Excel">
          <IconButton
            color="primary"
            onClick={() => setOpenUploadDialog(true)}
            aria-label="Bulk Upload"
            sx={{ fontSize: 32 }}
          >
            <CloudUpload fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Candidate">
          <IconButton
            color="secondary"
            onClick={() => setOpenForm(true)}
            aria-label="Add Candidate"
            sx={{ fontSize: 32 }}
          >
            <Add fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Assessment Excel</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Please upload your Excel file in the given template format.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>

            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFile />}
            >
              Choose File
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
              />
            </Button>

            {fileName && (
              <Typography color="success.main">Selected: {fileName}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)}>Cancel</Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleUploadConfirm}
            disabled={!fileName || isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {showAlert && (
        <Alert severity="success" sx={{ mb: 3 }}>
          File uploaded successfully!
        </Alert>
      )}

      {rows.length > 0 ? (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            {/* Left side: search */}
            <TextField
              placeholder="Search candidates..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />

            {/* Right side: action buttons */}
            <Box display="flex" alignItems="center" gap={2}>
              <Tooltip title="Download Preview...">
                <IconButton
                  onClick={handleOpenDownloadMenu}
                  sx={{
                    color: "#000",
                    "&:hover": {
                      color: "#5e5c4aff",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={downloadAnchorEl}
                open={openDownloadMenu}
                onClose={handleCloseDownloadMenu}
                PaperProps={{
                  elevation: 4,
                  sx: {
                    borderRadius: 2,
                    mt: 1,
                    minWidth: 200,
                    "& .MuiMenuItem-root": {
                      fontSize: 14,
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: "primary.light",
                        color: "white",
                      },
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleCloseDownloadMenu();
                    handleDownloadExcel();
                  }}
                >
                  Download as Excel (.xlsx)
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    handleCloseDownloadMenu();
                    handleDownloadPDF();
                  }}
                >
                  Download as PDF (.pdf)
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    handleCloseDownloadMenu();
                    handleDownloadJSON();
                  }}
                >
                  Download as JSON (.json)
                </MenuItem>
              </Menu>

              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveScores}
                disabled={!isTableEdited}
              >
                Save Changes
              </Button>

              <Button
                variant="contained"
                color="success"
                startIcon={<UploadFile />}
                onClick={handlePublish}
              >
                Publish Selected
              </Button>
            </Box>
          </Box>

          <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: "60vh" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {/* Select All Checkbox */}
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length < filteredRows.slice(1).length
                        }
                        checked={
                          filteredRows.length > 1 &&
                          selectedRows.length === filteredRows.slice(1).length
                        }
                        onChange={handleToggleAll}
                      />
                    </TableCell>

                    {rows[0].map((col, index) => (
                      <TableCell key={index} align="center">
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRows.slice(1).map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(rowIndex)}
                          onChange={() => toggleRowSelection(rowIndex)}
                        />
                      </TableCell>

                      {row.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          align="center"
                          sx={
                            rows[0][cellIndex] === "Publish Status"
                              ? {
                                  fontWeight:
                                    cell === "Published" ? 600 : "normal",
                                  color:
                                    cell === "Published" ? "green" : "inherit",
                                }
                              : {}
                          }
                        >
                          {rows[0][cellIndex] === "Publish Status" ? (
                            <Typography>{cell || ""}</Typography>
                          ) : (
                            <Typography
                              contentEditable
                              suppressContentEditableWarning
                              onBlur={(e) =>
                                handleCellChange(
                                  rowIndex + 1,
                                  cellIndex,
                                  e.target.innerText
                                )
                              }
                            >
                              {cell || ""}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredRows.length - 1}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      ) : (
        <Typography variant="h6" color="text.secondary" align="center">
          No assessment data uploaded
        </Typography>
      )}

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Candidate Assessment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {Object.keys(formData)
              .filter((key) => key !== "scoreArray")
              .map((field) => (
                <Grid item xs={6} key={field}>
                  <TextField
                    label={field}
                    name={field}
                    value={formData[field]}
                    onChange={handleFormChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
              ))}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                NOS Details
              </Typography>
              {formData.scoreArray.map((nos, index) => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  key={index}
                  sx={{ mb: 1 }}
                >
                  {Object.keys(nos).map((field) => (
                    <Grid item xs={2.4} key={field}>
                      <TextField
                        label={field}
                        value={nos[field]}
                        onChange={(e) =>
                          handleNOSChange(index, field, e.target.value)
                        }
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  ))}
                  <Grid item>
                    <Button color="error" onClick={() => removeNOSField(index)}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button onClick={addNOSField}>+ Add NOS</Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          {snackbar.loading && (
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
          )}
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssessmentScore;
